import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { matchQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Spinner } from '@ds';
import { RoutePlannerModal } from '@lp/features/RoutePlanner/components/RouteModals/components/RoutePlannerModal';
import { SelectCasesType } from '@lp/features/MultiSuggest/types';
import {
  invalidateInitialCase,
  invalidateTargetCases,
  InvalidateTargetCasesType,
} from '@lp/features/MultiSuggest/utils';
import { useInitialCaseFromParams } from '@lp/features/MultiSuggest/hooks';
import { Case } from '@/api/apis/case';
import { queryClient } from '@/queries';
import { multiSuggestKeys, useMultiSuggestionCandidatesQuery, useStartSuggestion } from '@/queries/multi-suggest';
import { retrieveUnifiedError } from '@/api/utils';
import { InitialCasesList } from './InitialCasesList';
import { TargetCasesList } from './TargetCasesList';
import { ActionButtons } from './ActionButtons';
import { SelectedCasesInfo } from './SelectedCasesInfo';

const SELECT_CASES_MODAL_HEADER_MAP: Record<SelectCasesType, string> = {
  [SelectCasesType.Initial]: 'Select Initial Case',
  [SelectCasesType.Target]: 'Select Target Cases',
};

type SelectCasesModalProps = {
  isOpen: boolean;
  onCloseModal: () => void;
};

export const SelectCasesModal = ({ isOpen, onCloseModal }: SelectCasesModalProps) => {
  const navigate = useNavigate();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [modalContentType, setModalContentType] = useState<SelectCasesType>(SelectCasesType.Initial);
  const [selectedInitialCase, setSelectedInitialCase] = useState<Case | null>(null);
  const [selectedTargetCases, setSelectedTargetCases] = useState<Case[]>([]);
  const [invalidInitialCaseUnosId, setInvalidInitialCaseUnosId] = useState<string | null>(null);
  const [invalidTargetCases, setInvalidTargetCases] = useState<InvalidateTargetCasesType[]>([]);
  const { data, isLoading, isFetching, refetch } = useMultiSuggestionCandidatesQuery();
  const { mutateAsync } = useStartSuggestion();
  const { data: initialCaseParams, clearParams } = useInitialCaseFromParams();

  const handleInitialCaseSelect = useCallback((caseItem: Case) => {
    setSelectedInitialCase((prev) => (prev?.caseId === caseItem.caseId ? null : caseItem));
    setInvalidInitialCaseUnosId(null);
  }, []);

  const handleTargetCasesSelect = useCallback((caseItem: Case) => {
    setSelectedTargetCases((prev) => {
      if (prev.some((c) => c.caseId === caseItem.caseId)) {
        return prev.filter((c) => c.caseId !== caseItem.caseId);
      }

      return [...prev, caseItem];
    });
    setInvalidTargetCases([]);
  }, []);

  const validateInitialCase = useCallback(
    (casesList: Case[]): boolean => {
      if (!selectedInitialCase) return false;

      const invalidUnosId = invalidateInitialCase(casesList, selectedInitialCase);

      if (invalidUnosId) {
        setInvalidInitialCaseUnosId(invalidUnosId);

        return false;
      }

      return true;
    },
    [selectedInitialCase],
  );

  const validateTargetCases = useCallback(
    (casesList: Case[]): InvalidateTargetCasesType[] => {
      if (!selectedTargetCases.length) return [];

      const invalidCases = invalidateTargetCases(casesList, selectedTargetCases);

      if (invalidCases.length) {
        setInvalidTargetCases(invalidCases);

        return invalidCases;
      }

      return [];
    },
    [selectedTargetCases],
  );

  const handleInitialCasesList = useCallback(async () => {
    const list = await refetch();
    const isValid = validateInitialCase(list.data?.initialCases || []);

    if (!isValid) {
      setSelectedInitialCase(null);
    }

    setSelectedTargetCases([]);
    setModalContentType(SelectCasesType.Initial);
  }, [refetch, validateInitialCase]);

  const handleTargetCasesList = useCallback(async () => {
    const list = await refetch();
    const isValid = validateInitialCase(list.data?.initialCases || []);

    if (isValid) {
      setModalContentType(SelectCasesType.Target);
    } else {
      setSelectedInitialCase(null);
    }
  }, [refetch, validateInitialCase]);

  const handleProceed = useCallback(async () => {
    if (!selectedInitialCase || !selectedTargetCases.length) return;

    try {
      setIsSubmitLoading(true);
      const list = await refetch();
      const isInitialValid = validateInitialCase(list.data?.initialCases || []);

      if (!isInitialValid) {
        setModalContentType(SelectCasesType.Initial);
        setSelectedInitialCase(null);
        return;
      }

      const invalidTarget = validateTargetCases(list.data?.targetCases || []);

      if (invalidTarget.length) {
        setSelectedTargetCases((prev) => {
          return prev.filter((c) => !invalidTarget.some((ic) => ic.caseId === c.caseId));
        });
        return;
      }

      const response = await mutateAsync({
        initiatedDate: new Date().toISOString(),
        initialCaseId: selectedInitialCase.caseId,
        targetCaseIds: selectedTargetCases.map((c) => c.caseId),
      });

      toast.info('Multi-Case Suggestion started.');

      if (response.id) {
        navigate(`/multi-case-suggest/${response.id}`);
      }
    } catch (e) {
      const error = retrieveUnifiedError(e);
      // Send error to Sentry
      // eslint-disable-next-line no-console
      console.log('Multi-case suggestion start error: ', e, error);

      toast.error(error.message);
    } finally {
      setIsSubmitLoading(false);
    }
  }, [
    mutateAsync,
    navigate,
    refetch,
    selectedInitialCase,
    selectedTargetCases,
    validateInitialCase,
    validateTargetCases,
  ]);

  const handleRemoveCaseChip = useCallback(
    (caseItem: Case) => {
      if (modalContentType === SelectCasesType.Initial) {
        handleInitialCaseSelect(caseItem);
      } else {
        handleTargetCasesSelect(caseItem);
      }
    },
    [modalContentType, handleInitialCaseSelect, handleTargetCasesSelect],
  );

  useEffect(() => {
    if (!isLoading && initialCaseParams?.caseId) {
      const selectedCase = data?.initialCases?.find((c) => c.caseId === initialCaseParams.caseId);

      if (selectedCase) {
        setSelectedInitialCase(selectedCase);
        setModalContentType(SelectCasesType.Target);
      } else {
        setInvalidInitialCaseUnosId(initialCaseParams.caseName);
      }

      clearParams();
    }
  }, [initialCaseParams, data, isLoading, clearParams]);

  const showLoader = isLoading || isFetching || isSubmitLoading;

  const isNextDisabled = useMemo(() => {
    if (modalContentType === SelectCasesType.Initial) {
      return !selectedInitialCase || !!invalidInitialCaseUnosId;
    }

    return !selectedTargetCases.length || !!invalidTargetCases.length;
  }, [invalidInitialCaseUnosId, invalidTargetCases, modalContentType, selectedInitialCase, selectedTargetCases]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        predicate: (query) => {
          return matchQuery({ queryKey: multiSuggestKeys.getCandidates(), exact: true }, query);
        },
      });
    };
  }, []);

  return (
    <RoutePlannerModal
      size="5xl"
      baseClassName="max-w-[1246px]"
      headerTitleClassName="text-hm! pt-4 pb-0"
      closeButtonClassName="text-base-10 m-2"
      bodyClassName="px-4 pb-4"
      title={SELECT_CASES_MODAL_HEADER_MAP[modalContentType]}
      isOpen={isOpen}
      onClose={onCloseModal}
    >
      <div className="relative border-t-1 border-b-1 border-base-60 py-4 flex flex-1 overflow-hidden">
        {showLoader && (
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-base-80/50 flex items-center justify-center z-30">
            <Spinner size="lg" />
          </div>
        )}
        {!!data?.initialCases?.length && (
          <div className="absolute top-4 left-0">
            <SelectedCasesInfo
              type={modalContentType}
              initialCase={selectedInitialCase}
              targetCases={selectedTargetCases}
              onRemoveCase={handleRemoveCaseChip}
            />
          </div>
        )}
        <InitialCasesList
          isVisible={modalContentType === SelectCasesType.Initial}
          cases={data?.initialCases || []}
          selectedCase={selectedInitialCase}
          invalidUnosId={invalidInitialCaseUnosId}
          onCaseSelect={handleInitialCaseSelect}
        />
        {modalContentType === SelectCasesType.Target && !!selectedInitialCase && (
          <TargetCasesList
            cases={data?.targetCases || []}
            initialCaseId={selectedInitialCase.caseId}
            selectedCases={selectedTargetCases}
            onCaseSelect={handleTargetCasesSelect}
            invalidCases={invalidTargetCases}
          />
        )}
      </div>
      <ActionButtons
        type={modalContentType}
        isNextDisabled={isNextDisabled}
        isProceedLoading={isSubmitLoading}
        onPrevPage={handleInitialCasesList}
        onNextPage={handleTargetCasesList}
        onProceed={handleProceed}
        onClose={onCloseModal}
      />
    </RoutePlannerModal>
  );
};
